
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { FormValues } from '../schema/QuestFormSchema';
import { Badge } from '@/types/quests';
import DateTimePickerField from '@/components/admin/common/DateTimePickerField';

interface QuestRewardsFieldsProps {
  form: UseFormReturn<FormValues>;
  badges: Badge[];
}

const QuestRewardsFields: React.FC<QuestRewardsFieldsProps> = ({ form, badges }) => {
  const { t } = useTranslation(['admin', 'quests', 'common']);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name="points_awarded"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('admin:pointsAwarded')}</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                type="number" 
                min={1} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="end_date"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('admin:expiryDate')}</FormLabel>
            <FormControl>
              <DateTimePickerField
                value={field.value}
                onChange={field.onChange}
                placeholder={t('admin:selectExpiryDate', 'Select expiry date')}
                clearable={true}
              />
            </FormControl>
            <FormDescription>
              {t('admin:leaveBlankForNoExpiry')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="badge_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('admin:associatedBadge')}</FormLabel>
            <Select 
              onValueChange={field.onChange} 
              value={field.value || "null"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('admin:selectBadge')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="null">{t('admin:noBadge')}</SelectItem>
                {badges.map(badge => (
                  <SelectItem key={badge.id} value={badge.id}>
                    {badge.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              {t('admin:optionalBadgeAwarded')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default QuestRewardsFields;
